import React, { useState } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
    Box, Text, Button, Flex, Input, RadioGroup, Radio,
    Divider
} from '@chakra-ui/react';
import MultiSelectTypeahead from '../../../../components/AutoCompleteInput/MultiSelectTypeahead';
import { useForm, Resolver } from 'react-hook-form';
import { CustomToast } from '../../../../components';
import { useFetch } from '../../../../hooks/useFetch';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

interface IProps {
    isModalOpen: boolean;
    handleCloseModal: () => void;
    selectedCandidateDetails: ICandidateSchedule[];
    setSelectedCandidateDetails: React.Dispatch<React.SetStateAction<ICandidateSchedule[]>>;
    jobId: number;
    loadCandidateList: () => void;
}

export interface ICandidateSchedule {
    id: number;
    candidate_Name: string;
    job_Profile_Name: string;
    interview_Date?: string;
    interview_Time?: string;
    mode?: "virtual" | "face-to-face";
    meeting_Link?: string;
}

interface IScheduleForm {
    interviewers: { employeeID: number; interviewer_Name: string }[];
}

const schema = yup.object().shape({
    interviewers: yup.array().min(1, "Please select at least one interviewer").required(),
});

export default function ScheduleMultipleInterview({ isModalOpen, handleCloseModal, selectedCandidateDetails, setSelectedCandidateDetails, jobId, loadCandidateList }: IProps) {
    const { addToast } = CustomToast();
    const { fetchApi, loading } = useFetch(addToast);
    const [interviewersOptions, setInterviewersOptions] = useState<any[]>([]);
    const [statusDisable] = useState(false);

    const { handleSubmit, control, watch, formState: { errors } } = useForm<IScheduleForm>({
        resolver: yupResolver(schema) as Resolver<IScheduleForm>,
        defaultValues: { interviewers: [] },
        mode: "onBlur",
    });

    const loadInterviewers = async (query?: string) => {
        if (!query) {
            setInterviewersOptions([]);
            return;
        }
        const response = await fetchApi("RecruitmentDropDown/interviewerList", "GET", "", `filter=${query}`, "");
        if (response) {
            setInterviewersOptions(response);
        }
    };

    const handleInterviewSubmit = async () => {
        if (selectedCandidateDetails.length === 0) {
            addToast({ message: "No candidates selected", status: "error" });
            return;
        }

        const interviewers = watch("interviewers");
        if (!interviewers || interviewers.length === 0) {
            addToast({ message: "Please select at least one interviewer", status: "error" });
            return;
        }

        for (const candidate of selectedCandidateDetails) {
            if (!candidate.interview_Date || !candidate.interview_Time || !candidate.mode) {
                addToast({ message: `Please fill interview details for ${candidate.candidate_Name}`, status: "error" });
                return;
            }
            if (candidate.mode === "virtual" && !candidate.meeting_Link) {
                addToast({ message: `Please enter meeting link for ${candidate.candidate_Name}`, status: "error" });
                return;
            }
        }

        const payload = selectedCandidateDetails.map(candidate => ({
            candidate_Id: candidate.id,
            job_Id: jobId,
            interview_Date: candidate.interview_Date,
            interview_Time: candidate.interview_Time,
            mode: candidate.mode,
            meeting_Link: candidate.meeting_Link || "",
            interviewers: interviewers.map(i => i.employeeID),
        }));

        const response = await fetchApi("Candidate/scheduleMultipleInterviews", "POST", payload, "", "");
        if (response) {
            addToast({ message: "Interview scheduled successfully", status: "success" });
            handleCloseModal();
            loadCandidateList();
            setSelectedCandidateDetails([]);
        }
    };

    return (
        <Modal isOpen={isModalOpen} onClose={handleCloseModal} size="xl" isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="lg" p={2}>
                <ModalHeader textAlign="center">Schedule Interview</ModalHeader>
                <ModalCloseButton />
                <ModalBody maxH="70vh" overflowY="auto">
                    <Box mb={5}>
                        <MultiSelectTypeahead loading={loading} disabled={statusDisable} name="interviewers" control={control} options={interviewersOptions} onCompletion={loadInterviewers} optionLabel="interviewer_Name" optionValue="employeeID" placeholder="Search interviewer..." />
                        {errors.interviewers && <Text color="red.500">{errors.interviewers.message}</Text>}
                    </Box>

                    {selectedCandidateDetails.length > 0 ? (
                        selectedCandidateDetails.map((candidate, idx) => (
                            <Box key={candidate.id} p={3} borderWidth="1px" borderRadius="md" mb={3}>
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Text className="font-poppins text_md text_medium" color="gray.800">{candidate.candidate_Name}</Text>
                                    <Text className="font-poppins text_sm text_regular" color="gray.600">{candidate.job_Profile_Name}</Text>
                                </Flex>
                                <Divider mb={3} />

                                <Flex gap={4} flexWrap="wrap">
                                    <Box flex="1">
                                        <Text fontSize="sm" mb={1} color="gray.700">Interview Date</Text>
                                        <Input type="date" value={candidate.interview_Date || ""} onChange={(e) => {
                                            const updated = selectedCandidateDetails.map(c => c.id === candidate.id ? { ...c, interview_Date: e.target.value } : c);
                                            setSelectedCandidateDetails(updated);
                                        }}
                                        />
                                    </Box>
                                    <Box flex="1">
                                        <Text fontSize="sm" mb={1} color="gray.700">Interview Time</Text>
                                        <Input type="time" value={candidate.interview_Time || ""} onChange={(e) => {
                                            const updated = selectedCandidateDetails.map(c =>
                                                c.id === candidate.id ? { ...c, interview_Time: e.target.value } : c
                                            );
                                            setSelectedCandidateDetails(updated);
                                        }}
                                        />
                                    </Box>
                                </Flex>
                                <Box mt={4} display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                                    <Text fontSize="sm" mt={2} color="gray.700">Mode of Interview</Text>
                                    <Flex gap={6}>
                                        <RadioGroup value={candidate.mode || ""} onChange={(value: any) => {
                                            const updated = selectedCandidateDetails.map(c =>
                                                c.id === candidate.id ? { ...c, mode: value } : c
                                            );
                                            setSelectedCandidateDetails(updated);
                                        }}
                                        >
                                            <Flex gap={4} mt={2}>
                                                <Radio value="virtual">Virtual</Radio>
                                                <Radio value="face-to-face">Face-to-Face</Radio>
                                            </Flex>
                                        </RadioGroup>
                                    </Flex>
                                </Box>


                                {candidate.mode === "virtual" && (
                                    <Box mt={3}>
                                        <Text fontSize="sm" mb={1} color="gray.700">Meeting Link</Text>
                                        <Input mt={2} placeholder="Enter Meeting Link" value={candidate.meeting_Link || ""} onChange={(e) => {
                                            const updated = selectedCandidateDetails.map(c =>
                                                c.id === candidate.id ? { ...c, meeting_Link: e.target.value } : c
                                            );
                                            setSelectedCandidateDetails(updated);
                                        }}
                                        />
                                    </Box>

                                )}
                            </Box>
                        ))
                    ) : (
                        <Text textAlign="center" color="gray.500">No candidates selected</Text>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Flex gap={3}>
                      <Button onClick={handleInterviewSubmit} isDisabled={loading} className="btn_theme btn_theme:hover" >
                            Schedule Interview
                        </Button>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
